import { useState, useEffect, useRef, useCallback } from "react";
import { useChat } from "@ai-sdk/react";
import {
  isTextUIPart,
  type ChatOnFinishCallback,
  type UIMessage,
} from "ai";
import { restoreMessages } from "@/components/cowork/restore-messages";
import { useCopilotStore } from "@/stores/use-copilot-store";
import { useAuth } from "@/hooks/use-auth";
import { useCopilotCapabilities } from "@/hooks/use-copilot-capabilities";
import { useFetchAiSettingsQuery } from "@/hooks/ai-settings/use-fetch-ai-settings-query";
import { usePageContext } from "@/hooks/use-page-context";
import { useSttProvider } from "@/hooks/use-stt-provider";
import { APP_CONSTANTS } from "@/config";
import {
  getCopilotBffHeaders,
  getCopilotBffUrl,
  getCopilotMediaRejection,
  getCopilotUnavailableMessage,
  isCopilotStreamError,
  COPILOT_STREAM_ERROR_MESSAGE,
  type CopilotUnavailableError,
} from "@/lib/copilot-api";
import { captureCopilotOperation, type CopilotOperation } from "@/lib/copilot-session-fetch";
import { createCopilotChatTransport } from "@/lib/copilot-chat-transport";
import { prepareCopilotSendRequest } from "@/lib/copilot-transport";
import {
  attachmentStub,
  prepareMediaFiles,
  toFileParts,
  type CopilotFilePart,
} from "@/lib/media-prep";
import * as convApi from "@/services/conversations";
import type { Conversation } from "@/components/cowork/types";
import { toast } from "@/hooks/use-toast";
import { MEDIA_LIMITS } from "@/lib/media-limits";
import { DROP_COPY, acceptDroppedFiles } from "@/lib/drop-files";

export type FilePreviewItem = {
  file: File;
  previewUrl?: string;
};

const MAX_FILES = MEDIA_LIMITS.maxAttachments;

export type AgentType = "copilot" | "sdr";
type TurnFinalizer = { operation: CopilotOperation; conversationKey: string; voice: boolean };

export function useCoworkChat(opts: { agentType?: AgentType } = {}) {
  const agentType: AgentType = opts.agentType ?? "copilot";
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const loadSequence = useRef(0);
  const [attachedFiles, setAttachedFiles] = useState<FilePreviewItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dropNotice, setDropNotice] = useState<string | null>(null);
  const [uploadingStatus, setUploadingStatus] = useState<string | null>(null);
  const [inputText, setInputText] = useState("");
  const [aiContext, setAiContext] = useState<convApi.AiContext | null>(null);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [copilotUnavailable, setCopilotUnavailable] = useState<CopilotUnavailableError | null>(null);
  // The runtime's own pt-BR sentence for a refused attachment (422/413).
  const [mediaRejection, setMediaRejection] = useState<string | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);
  // TENANT-AIKEY-REQUIRED-01: only an explicit `false` means the clinic has no
  // key; a failed or older read states nothing.
  const aiKeyMissing = useFetchAiSettingsQuery().data?.setting?.has_api_key === false;

  const uploadingOperation = useRef<CopilotOperation | null>(null);
  const dragCounterRef = useRef(0);
  const streamConvIdRef = useRef<string | null>(null);
  const operationTurns = useRef(new WeakMap<CopilotOperation, TurnFinalizer>());
  const finalizers = useRef(new Map<string, TurnFinalizer>());
  const conversationIds = useRef(new Map<string, string>());
  const pendingMessagesRef = useRef<{ localId: string; content: string; role: "user" | "assistant"; toolCalls?: unknown[] }[]>([]);
  const localIdsRef = useRef<Set<string>>(new Set());
  const lastPersistedAssistantIdRef = useRef<string | null>(null);

  const { accessToken: token, user } = useAuth();
  const tenantId = localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY) || "";
  const sessionKey = `${agentType}:${tenantId}:${user?.id ?? ""}`;
  const previousSession = useRef(sessionKey);
  const capabilityQuery = useCopilotCapabilities();
  const { capabilities } = capabilityQuery;
  const { continuation, clearContinuation } = useCopilotStore();
  const { provider: sttProvider } = useSttProvider();

  // The hint travels with the turn, and the transport is built once — so the
  // send path reads the CURRENT screen through a ref, never the one the drawer
  // happened to be mounted on (SCRUM-306).
  const currentPageContext = usePageContext();
  const pageContext = continuation ? null : currentPageContext;
  const pageContextRef = useRef(pageContext);
  pageContextRef.current = pageContext;
  const turnContexts = useRef(new Map<string, typeof pageContext>());

  // Single transport instance — evaluates auth storage for every request.
  const [transport] = useState(
    () => createCopilotChatTransport({
      // All chat traffic goes through /copilot/chat. agentType only isolates
      // conversation threads; it does not switch backend endpoints.
      api: getCopilotBffUrl("chat"),
      onAssistantStart: (messageId, operation) => {
        const turn = operationTurns.current.get(operation);
        if (turn && operation.isCurrent() && !finalizers.current.has(messageId)) finalizers.current.set(messageId, turn);
      },
      // Attachments belong to the turn that carried them (D-B2): earlier
      // messages travel as a text stub, never as a data URL again.
      prepareSendMessagesRequest: (options) => {
        const lastUser = [...options.messages].reverse().find((message) => message.role === "user");
        const turnId = options.messageId ?? lastUser?.id ?? options.id;
        if (!turnContexts.current.has(turnId)) turnContexts.current.set(turnId, pageContextRef.current);
        return prepareCopilotSendRequest({ ...options, pageContext: turnContexts.current.get(turnId) });
      },
    })
  );

  // Helper to play TTS audio
  const playTTS = async (text: string, operation: CopilotOperation) => {
    try {
      operation.assertCurrent();
      const res = await operation.fetch(getCopilotBffUrl("voice/synthesize"), {
        method: "POST",
        headers: { ...operation.headers, "Content-Type": "application/json" },
        body: JSON.stringify({ text, voice: "nova", format: "mp3" }),
      });
      if (res.ok) {
        const audioBlob = await res.blob();
        operation.assertCurrent();
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        await audio.play();
      }
    } catch (err) {
      console.error("TTS playback error", err);
    }
  };

  const isVoiceModeRef = useRef(isVoiceMode);
  isVoiceModeRef.current = isVoiceMode;

  function finishMessage(message: any, turn: TurnFinalizer) {
    const { operation, conversationKey } = turn;
    if (!operation.isCurrent()) return;
    const convId = conversationIds.current.get(conversationKey) ?? conversationKey;
    const turnToken = operation.headers.Authorization.slice("Bearer ".length);
    const turnTenant = operation.headers["X-Tenant"];

    const parts = message.parts || [];
    const textParts = parts
      .filter((p: any) => p.type === 'text')
      .map((p: any) => p.text)
      .join('');

    const isToolPart = (p: any) =>
      p.type === 'tool-invocation' ||
      (typeof p.type === 'string' && p.type.startsWith('tool-') && p.type !== 'tool-invocation');

    const toolParts = parts
      .filter((p: any) => isToolPart(p))
      .map((p: any) => {
        const name = p.type === 'tool-invocation'
          ? p.toolInvocation?.toolName
          : (p.toolName || p.type.replace(/^tool-/, ''));
        return `[Tool: ${name}]`;
      })
      .join(' ');

    const content = [textParts, toolParts].filter(Boolean).join('\n');
    const finalContent = content || (toolParts ? `[Resultado visual: ${toolParts}]` : '') || message.content || '';

    if (turn.voice && textParts) {
      void playTTS(textParts, operation);
    }

    if (!finalContent) return;

    const toolCalls = parts
      .filter((p: any) => isToolPart(p))
      .map((p: any) => {
        const isV6 = p.type !== 'tool-invocation' && p.type?.startsWith('tool-');
        return {
          tool_name: isV6 ? (p.toolName || p.type.replace(/^tool-/, '')) : (p.toolInvocation?.toolName || ''),
          tool_call_id: isV6 ? p.toolCallId : (p.toolInvocation?.toolCallId || ''),
          args: isV6 ? p.input : (p.toolInvocation?.args || {}),
          state: isV6 ? p.state : (p.toolInvocation?.state || ''),
          result: isV6 ? p.output : (p.toolInvocation?.result || undefined),
        };
      });

    const tcData = toolCalls.length > 0 ? toolCalls : undefined;

    if (localIdsRef.current.has(convId)) {
      pendingMessagesRef.current.push({ localId: convId, content: finalContent, role: "assistant", toolCalls: tcData });
    } else {
      convApi.addMessageWithRetry(turnToken, turnTenant, convId, "assistant", finalContent, tcData)
        .then((res) => {
          if (!operation.isCurrent()) return;
          if (res) {
            lastPersistedAssistantIdRef.current = message.id || null;
          } else {
            toast({ title: "Erro ao salvar resposta", description: "A resposta pode não aparecer ao recarregar a conversa.", variant: "destructive" });
          }
        });
    }
  };

  const handleChatFinish: ChatOnFinishCallback<UIMessage> = ({ message, isError, isAbort, isDisconnect }) => {
    const turn = finalizers.current.get(message.id);
    finalizers.current.delete(message.id);
    if (!turn || !turn.operation.isCurrent() || isError || isAbort || isDisconnect) return;
    setCopilotUnavailable(null);
    setMediaRejection(null);
    setStreamError(null);
    finishMessage(message, turn);
  };

  const { messages: aiMessages, sendMessage: sdkSendMessage, setMessages, status } = useChat({
    transport,
    id: sessionKey,
    onFinish: handleChatFinish,
    onError: (error) => {
      // A refused attachment is the runtime answering, not an outage: it
      // carries its own pt-BR sentence and must be shown verbatim.
      const rejection = getCopilotMediaRejection(error);
      if (rejection) {
        setMediaRejection(rejection);
        return;
      }
      // A run that died mid-stream is answered inside the thread, with a
      // retry that re-sends the same turn (SPEC-COPILOT-02, "Provider 503").
      if (isCopilotStreamError(error)) {
        setStreamError(COPILOT_STREAM_ERROR_MESSAGE);
        return;
      }
      const unavailable = getCopilotUnavailableMessage(error);
      if (unavailable) {
        setCopilotUnavailable(unavailable);
      }
    },
  });

  function registerTurn(operation: CopilotOperation, conversationKey: string | null) {
    operation.assertCurrent();
    if (conversationKey && !operationTurns.current.has(operation)) {
      operationTurns.current.set(operation, { operation, conversationKey, voice: isVoiceModeRef.current });
    }
  }

  function sendMessage(
    message: Parameters<typeof sdkSendMessage>[0],
    options?: Parameters<typeof sdkSendMessage>[1],
    operation: CopilotOperation = captureCopilotOperation(),
  ) {
    registerTurn(operation, streamConvIdRef.current);
    return sdkSendMessage(message, { ...options, metadata: operation });
  }

  // ── Helpers ──

  useEffect(() => {
    if (previousSession.current === sessionKey) return;
    previousSession.current = sessionKey;
    loadSequence.current += 1;
    streamConvIdRef.current = null;
    pendingMessagesRef.current = [];
    localIdsRef.current.clear();
    turnContexts.current.clear();
    finalizers.current.clear();
    conversationIds.current.clear();
    setMessages([]);
    setActiveConvId(null);
    setConversations([]);
    setAiContext(null);
    setInputText("");
    setAttachedFiles([]);
    setLoadingMessages(false);
    uploadingOperation.current = null;
    setUploadingStatus(null);
    setConversationError(null);
  }, [sessionKey, setMessages]);

  function bumpConversation(id: string) {
    setConversations((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      if (idx <= 0) return prev;
      const conv = { ...prev[idx], created: new Date() };
      return [conv, ...prev.slice(0, idx), ...prev.slice(idx + 1)];
    });
  }

  function ensureConversation(title?: string, operation = captureCopilotOperation()): string {
    operation.assertCurrent();
    if (activeConvId) {
      bumpConversation(activeConvId);
      return activeConvId;
    }

    const localId = crypto.randomUUID();
    const convTitle = title ? title.slice(0, 50) : "Nova conversa";

    const conv: Conversation = {
      id: localId,
      title: convTitle,
      messages: [],
      created: new Date(),
    };

    setConversations((prev) => [conv, ...prev]);
    setActiveConvId(localId);
    localIdsRef.current.add(localId);

    if (token && tenantId) {
      convApi.createConversation(token, tenantId, convTitle).then((remote) => {
        if (remote && operation.isCurrent()) {
          conversationIds.current.set(localId, remote.id);
          setConversations((prev) =>
            prev.map((c) => (c.id === localId ? { ...c, id: remote.id } : c))
          );
          setActiveConvId((cur) => (cur === localId ? remote.id : cur));
          if (streamConvIdRef.current === localId) {
            streamConvIdRef.current = remote.id;
          }
          localIdsRef.current.delete(localId);
          const pending = pendingMessagesRef.current.filter((m) => m.localId === localId);
          pendingMessagesRef.current = pendingMessagesRef.current.filter((m) => m.localId !== localId);
          for (const msg of pending) {
            convApi.addMessageWithRetry(token, tenantId, remote.id, msg.role, msg.content, msg.toolCalls).catch(() => {});
          }
        }
      }).catch(() => {});
    }

    return localId;
  }

  function persistUserMessage(convId: string, content: string) {
    bumpConversation(convId);
    if (!token || !tenantId || !convId) return;

    if (localIdsRef.current.has(convId)) {
      pendingMessagesRef.current.push({ localId: convId, content, role: "user" });
    } else {
      convApi.addMessageWithRetry(token, tenantId, convId, "user", content).then((res) => {
        if (!res) {
          toast({ title: "Erro ao salvar mensagem", description: "Sua mensagem pode não aparecer ao recarregar.", variant: "destructive" });
        }
      });
    }
  }

  // ── Load persisted conversations ──
  useEffect(() => {
    if (!token || !tenantId) return;
    let active = true;
    convApi.listConversations(token, tenantId).then((list) => {
      if (!active || !list.length) return;
      const mapped: Conversation[] = list.map((c) => ({
        id: c.id,
        title: c.title || "Nova conversa",
        messages: [],
        created: new Date(c.updated_at || c.created_at),
      }));
      mapped.sort((a, b) => b.created.getTime() - a.created.getTime());
      setConversations(mapped);
    }).catch(() => {});

    // Also fetch AI Context (limits)
    convApi.getAiContext(token, tenantId).then((context) => { if (active) setAiContext(context); }).catch(() => {});
    return () => { active = false; };
  }, [token, tenantId]);

  // ── Safety net: save on page unload ──
  useEffect(() => {
    const handleBeforeUnload = () => {
      const convId = streamConvIdRef.current;
      if (!convId || !token || !tenantId || localIdsRef.current.has(convId)) return;

      const lastAssistant = [...aiMessages].reverse().find((m) => m.role === 'assistant');
      if (!lastAssistant || lastAssistant.id === lastPersistedAssistantIdRef.current) return;

      const content = lastAssistant.parts
        ?.filter((p: any) => p.type === 'text')
        .map((p: any) => p.text)
        .join('') || (lastAssistant as any).content || '';
      if (!content) return;

      convApi.addMessageBeacon(token, tenantId, convId, "assistant", content);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [aiMessages, token, tenantId]);

  const selectConversation = useCallback(async (id: string) => {
    if (status === "streaming" || status === "submitted") {
      setConversationError("Aguarde a resposta atual terminar. O contexto solicitado será carregado em seguida.");
      return;
    }
    const navigationIntent = useCopilotStore.getState().continuation;
    const sequence = ++loadSequence.current;
    setLoadingMessages(true);
    setConversationError(null);
    try {
      if (!token || !tenantId) throw new Error("Missing session");
      const data = await convApi.getConversation(token, tenantId, id);
      if (!data || !Array.isArray(data.messages)) throw new Error("Conversation unavailable");
      if (sequence !== loadSequence.current) return;
      setMessages(restoreMessages(data.messages));
      setActiveConvId(id);
      if (navigationIntent && navigationIntent.conversationId !== id
        && useCopilotStore.getState().continuation === navigationIntent) {
        // Its effect cleanup invalidates this load, so finish before releasing it.
        setLoadingMessages(false);
        clearContinuation();
      }
    } catch {
      if (sequence === loadSequence.current) setConversationError("Não foi possível carregar o contexto. Seu trabalho atual foi mantido. Tente carregar novamente.");
    } finally {
      if (sequence === loadSequence.current) setLoadingMessages(false);
    }
  }, [token, tenantId, setMessages, status, clearContinuation]);

  useEffect(() => {
    if (!continuation) return;
    if (continuation.tenantId !== tenantId || continuation.ownerId !== user?.id) {
      // A prior actor/clinic's navigation intent must not disable this session.
      clearContinuation();
      setConversationError(null);
      return;
    }
    if (activeConvId === continuation.conversationId) return;
    void selectConversation(continuation.conversationId);
    return () => { loadSequence.current += 1; };
  }, [continuation, tenantId, user?.id, activeConvId, selectConversation, clearContinuation]);

  function deleteConversation(id: string) {
    setConversations((prev) => {
      const filtered = prev.filter((c) => c.id !== id);
      if (activeConvId === id) {
        const next = filtered[0]?.id || null;
        setActiveConvId(next);
        if (next) selectConversation(next);
        else setMessages([]);
      }
      return filtered;
    });
    if (token && tenantId) {
      convApi.deleteConversation(token, tenantId, id).catch(() => {});
    }
  }

  // ── Regenerate last response ──
  function handleRegenerate() {
    if (isStreaming || aiMessages.length < 2) return;
    const lastUserIdx = [...aiMessages].reverse().findIndex((m) => m.role === "user");
    if (lastUserIdx === -1) return;
    const lastUserMsg = aiMessages[aiMessages.length - 1 - lastUserIdx];
    const userText = lastUserMsg.parts?.filter((p: any) => p.type === "text").map((p: any) => p.text).join("") || "";
    if (!userText) return;
    const trimmed = aiMessages.slice(0, aiMessages.length - lastUserIdx);
    setMessages(trimmed as any);
    streamConvIdRef.current = activeConvId;
    sendMessage({ text: userText });
  }

  function retryCopilot() {
    const lastUserMessage = [...aiMessages].reverse().find((message) => message.role === "user");
    if (!lastUserMessage) return;

    const text = lastUserMessage.parts
      ?.filter(isTextUIPart)
      .map((part) => part.text)
      .join("") || "";
    if (!text) return;

    setCopilotUnavailable(null);
    setStreamError(null);
    streamConvIdRef.current = activeConvId;
    sendMessage({ text, messageId: lastUserMessage.id });
  }

  /** Sends one user turn and stores its transcript.
   *
   * The transcript keeps the typed text plus one `[anexo: …]` stub per file
   * (D-B2): the data URL travels in this request and nowhere else, so it never
   * reaches the conversations table.
   */
  function sendCopilotMessage(
    text: string,
    convId: string,
    isNewConv: boolean,
    files: CopilotFilePart[] = [],
    transcript?: string,
    operation: CopilotOperation = captureCopilotOperation(),
  ) {
    registerTurn(operation, convId);
    streamConvIdRef.current = convId;
    persistUserMessage(convId, transcript ?? text);

    const payload: { text: string; files?: CopilotFilePart[] } | { files: CopilotFilePart[] } =
      text ? { text, ...(files.length > 0 ? { files } : {}) } : { files };

    if (isNewConv) {
      requestAnimationFrame(() => {
        if (!operation.isCurrent()) return;
        sendMessage(payload, undefined, operation);
      });
    } else {
      sendMessage(payload, undefined, operation);
    }
  }

  // ── Send message (text + optional files) ──
  //
  // Files leave as native AI SDK `file` parts carrying a `data:` URL, after
  // the allowlist, the downscale and the two size limits in media-prep. There
  // is no upload route in this path: media is inline and transient (D-B2).
  const handleSubmitWithFiles = useCallback(async () => {
    const hasText = inputText.trim().length > 0;
    const hasFiles = attachedFiles.length > 0;
    if (!hasText && !hasFiles) return;

    const operation = captureCopilotOperation();
    const text = inputText.trim();
    const currentFiles = [...attachedFiles];
    const isNewConv = !activeConvId;

    let fileParts: CopilotFilePart[] = [];
    let stubs = "";

    if (currentFiles.length > 0) {
      uploadingOperation.current = operation;
      setUploadingStatus("Preparando anexo…");
      try {
        const { files: prepared, errors } = await prepareMediaFiles(
          currentFiles.map((item) => item.file),
        );

        if (!operation.isCurrent()) return;
        for (const message of errors) {
          toast({ title: DROP_COPY.notSent, description: message, variant: "destructive" });
        }

        if (prepared.length > 0) {
          fileParts = await toFileParts(prepared);
          if (!operation.isCurrent()) return;
          stubs = prepared.map(attachmentStub).join(" ");
        }
        if (fileParts.length === 0 && !text) return;
      } finally {
        if (uploadingOperation.current === operation) {
          uploadingOperation.current = null;
          setUploadingStatus(null);
        }
      }
    }

    operation.assertCurrent();
    setInputText("");
    setAttachedFiles([]);
    setDropNotice(null);
    setStreamError(null);
    const convId = ensureConversation(text || "Anexo de arquivos", operation);
    const transcript = [text, stubs].filter(Boolean).join("\n");

    sendCopilotMessage(text, convId, isNewConv, fileParts, transcript, operation);
  }, [inputText, activeConvId, token, tenantId, attachedFiles, sendMessage]);

  // ── Dictated message: transcribe, then leave the text in the composer ──
  //
  // Only the tenants whose runtime transcribes get here; a browser with the
  // Web Speech API keeps its own recognition inside VoiceButton.
  async function handleAudioUpload(blob: Blob) {
    if (sttProvider !== "server") return;

    const file = new File([blob], `audio-${Date.now()}.webm`, { type: "audio/webm" });

    let operation: CopilotOperation | undefined;
    try {
      operation = captureCopilotOperation();
      uploadingOperation.current = operation;
      setUploadingStatus("Transcrevendo…");
      const formData = new FormData();
      formData.append("audio", file);

      const sttRes = await operation.fetch(getCopilotBffUrl("stt/transcribe"), {
        method: "POST",
        headers: getCopilotBffHeaders(),
        body: formData,
      });

      let transcription = "";
      if (sttRes.ok) {
        const sttData = await sttRes.json();
        operation.assertCurrent();
        transcription = sttData.text || "";
      }

      if (!transcription) {
        toast({
          title: "Não foi possível transcrever agora",
          description: "Digite a mensagem.",
          variant: "destructive",
        });
        return;
      }

      // The clinic reads what was heard before it is sent: a dictation lands
      // in the composer, editable, and is never auto-sent.
      operation.assertCurrent();
      setInputText((current) => (current ? `${current} ${transcription}` : transcription));
    } catch (err) {
      if (operation && !operation.isCurrent()) return;
      console.error("Audio transcription error", err);
      toast({
        title: "Não foi possível transcrever agora",
        description: "Digite a mensagem.",
        variant: "destructive",
      });
    } finally {
      if (operation && uploadingOperation.current === operation) {
        uploadingOperation.current = null;
        setUploadingStatus(null);
      }
    }
  }


  // ── Export functions ──
  function handleExportJSON() {
    if (!activeConvId || aiMessages.length === 0) return;
    const conv = conversations.find(c => c.id === activeConvId);
    const exportData = {
      id: activeConvId,
      title: conv?.title || "Conversa",
      exported_at: new Date().toISOString(),
      messages: aiMessages.map((msg: any) => ({
        role: msg.role,
        content: msg.parts?.filter((p: any) => p.type === 'text').map((p: any) => p.text).join('') || '',
        timestamp: msg.createdAt?.toISOString(),
        tools: msg.parts?.filter((p: any) =>
          p.type === 'tool-invocation' || (typeof p.type === 'string' && p.type.startsWith('tool-') && p.type !== 'tool-invocation')
        ).map((p: any) => ({
          name: p.type === 'tool-invocation' ? p.toolInvocation?.toolName : (p.toolName || p.type.replace(/^tool-/, '')),
          state: p.type === 'tool-invocation' ? p.toolInvocation?.state : p.state,
        })) || [],
      })),
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversa-${conv?.title?.slice(0, 20) || activeConvId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleExportText() {
    if (!activeConvId || aiMessages.length === 0) return;
    const conv = conversations.find(c => c.id === activeConvId);
    let text = `# ${conv?.title || "Conversa"}\n`;
    text += `Exportado em: ${new Date().toLocaleString("pt-BR")}\n\n---\n\n`;

    aiMessages.forEach((msg: any) => {
      const role = msg.role === 'user' ? 'Você' : 'Dr. Insights';
      const content = msg.parts?.filter((p: any) => p.type === 'text').map((p: any) => p.text).join('') || '';
      const time = msg.createdAt?.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) || '';
      text += `**${role}** ${time}\n${content}\n\n`;
    });

    const blob = new Blob([text], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversa-${conv?.title?.slice(0, 20) || activeConvId}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Suggestion click ──
  function handleSuggestionClick(s: string) {
    if (capabilityQuery.isError || capabilityQuery.isLoading || loadingMessages || conversationError) {
      setInputText(s);
      return;
    }
    const isNewConv = !activeConvId;
    const convId = ensureConversation(s);
    sendCopilotMessage(s, convId, isNewConv);
  }

  // ── Drag and drop handlers ──
  //
  // The panel is the drop zone (D5). Only file drags are handled: for them every
  // handler calls preventDefault, so the window drop guard the drawer installs
  // leaves a drop on the panel alone, and the counter counts file drags only.
  // Text dragged into the composer textarea keeps the browser default.
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes("Files")) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    // No overlay for a clinic whose Copilot reads no media: the drop answers
    // with a status instead of a control that cannot work (agent-experience §5).
    if (capabilities.inputModalities.length > 0) {
      setIsDragOver(true);
    }
  }, [capabilities]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes("Files")) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes("Files")) return;
    e.preventDefault();
    e.stopPropagation();
    // Always "copy", even for a clinic whose Copilot reads no media: "none"
    // would turn the drop into a dragleave and the status saying so would
    // never appear. The isDragOver gate keeps the overlay away instead.
    e.dataTransfer.dropEffect = "copy";
  }, []);

  // A paperclip or pasted file past the attachment limit is refused on the same
  // status line and toast as a drop (one notice channel for every entry point).
  const refuseAttachments = useCallback((notice: string) => {
    setDropNotice(notice);
    toast({ title: DROP_COPY.notSent, description: notice, variant: "destructive" });
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      // A drop ends every drag, file or not: no dragleave follows it.
      dragCounterRef.current = 0;
      setIsDragOver(false);
      if (!e.dataTransfer.types.includes("Files")) return;
      e.preventDefault();
      e.stopPropagation();

      const files = e.dataTransfer.files;
      if (!files || files.length === 0) return;
      // The same allowlist and count as the paperclip; size and downscale are
      // checked by prepareMediaFiles at send time, as for every entry point.
      const { files: accepted, notice } = acceptDroppedFiles(Array.from(files), {
        modalities: capabilities.inputModalities,
        attached: attachedFiles.length,
      });
      setDropNotice(notice);
      if (notice) toast({ title: DROP_COPY.notSent, description: notice, variant: "destructive" });
      if (accepted.length === 0) return;
      setAttachedFiles((prev) => [
        ...prev,
        ...accepted.map((file) => ({
          file,
          previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
        })),
      ]);
    },
    [attachedFiles, capabilities]
  );

  const isStreaming = status === 'streaming' || status === 'submitted';

  return {
    // State
    conversations,
    activeConvId,
    loadingMessages,
    attachedFiles,
    isDragOver,
    dropNotice,
    uploadingStatus,
    inputText,
    aiMessages,
    isStreaming,
    status,
    aiContext,
    isVoiceMode,
    copilotUnavailableMessage: copilotUnavailable?.message ?? null,
    copilotUnavailableReason: copilotUnavailable?.reason ?? null,
    aiKeyMissing,
    mediaRejectionMessage: mediaRejection,
    streamErrorMessage: streamError,
    capabilities,
    capabilitiesLoading: capabilityQuery.isLoading,
    capabilitiesError: capabilityQuery.isError,
    capabilitiesFetching: capabilityQuery.isFetching,
    retryCapabilities: capabilityQuery.retry,
    conversationError,
    retryConversation: () => { if (continuation) void selectConversation(continuation.conversationId); },
    contextLabel: continuation ? "Solicitação do centro de comando" : pageContext ? "Paciente da página atual" : "Sem paciente selecionado",
    sttProvider,

    // Setters
    setInputText,
    setAttachedFiles,
    setIsVoiceMode,

    // Actions
    selectConversation,
    deleteConversation,
    ensureConversation,
    handleSubmitWithFiles,
    handleAudioUpload,
    handleRegenerate,
    retryCopilot,
    handleSuggestionClick,
    handleExportJSON,
    handleExportText,
    sendMessage,

    // Drag & drop
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    refuseAttachments,

    // Constants
    MAX_FILES,
  };
}
